// Centralized user-data access & persistence utilities
// Ensures consistent shape and safe parsing.
// verifyPin persists user-data; other pages read it using these helpers.

export interface StoredUserData {
  docId?: string;
  data?: Record<string, unknown> & { docId?: string } | null; // legacy layering may embed data here
  body?: { data?: Record<string, unknown> & { docId?: string } } | null;
  [key: string]: unknown;
}

export interface NormalizedUser {
  docId?: string;
  // Spread of arbitrary user attributes retained from legacy storage; keep index signature typed as unknown
  [key: string]: unknown;
}

const KEY = 'user-data';

export const readRawUserData = (): StoredUserData | null => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const extractNormalizedUser = (stored: StoredUserData | null): NormalizedUser | null => {
  if (!stored) return null;
  // Prefer explicit fields > nested, keep falsy ("", 0) docId if explicitly set but treat undefined/null as fallback candidates
  const docId = stored.docId ?? stored.data?.docId ?? stored.body?.data?.docId;
  const base = (stored.data ?? stored.body?.data ?? stored) as Record<string, unknown>;
  return { ...base, docId };
};

export const getUserData = (): NormalizedUser | null => extractNormalizedUser(readRawUserData());

export const setUserData = (obj: StoredUserData) => {
  try { localStorage.setItem(KEY, JSON.stringify(obj)); } catch { /* ignore */ }
};

export const clearUserData = () => {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
};

/**
 * Clears stale localStorage caches including user-data and field-config-cache.
 * This is typically called when the metro config is refreshed to ensure cached data stays in sync.
 */
export const clearStaleCaches = (): void => {
  try {
    localStorage.removeItem('user-data');
    localStorage.removeItem('field-config-cache');
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('lastActivityTs');
    localStorage.removeItem("uploadRequiredSectionFieldNames");
  } catch {
    // Ignore localStorage errors (e.g., in environments where localStorage is not available)
  }
};

export const decrementMaxReachedStep = (): void => {
  const userData = getUserData();
  if (!userData) return;
  userData.maxReachedStep = userData.pageNumber === 6 ? (userData.pageNumber as number) - 1 : userData.maxReachedStep;
  setUserData(userData);
};